import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Mic, MicOff, Play, Send, Volume2, VolumeX, X } from "lucide-react";
import { useAssistant, STATUS_LABELS } from "../hooks/useAssistant";
import { useLanguage } from "../context/LanguageContext";
import { useT } from "../context/ContentContext";
import { useUI } from "../lib/i18n";

const BARS = [0, 1, 2, 3, 4, 5, 6];

export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const a = useAssistant();
  const { lang } = useLanguage();
  const t = useT();
  const ui = useUI();
  const transcriptRef = useRef(null);

  useEffect(() => {
    if (localStorage.getItem("consulentia-greeted")) return;
    const timer = setTimeout(() => {
      if (localStorage.getItem("consulentia-greeted")) return;
      localStorage.setItem("consulentia-greeted", "1");
      setOpen(true);
      a.greet(t("greeting") || ui.greeting);
    }, 2000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    transcriptRef.current?.scrollTo({ top: transcriptRef.current.scrollHeight, behavior: "smooth" });
  }, [a.messages]);

  const active = a.state === "listening" || a.state === "speaking";

  return (
    <>
      <motion.button
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.2, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        onClick={() => setOpen(!open)}
        data-testid="assistant-widget-toggle"
        aria-label="Apri assistente vocale"
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-colors overflow-hidden ${
          open ? "bg-white/10 border border-white/20 text-white" : t("assistant_icon") ? "ring-2 ring-[#007AFF]/60 hover:ring-[#007AFF]" : "bg-[#007AFF] text-white hover:bg-white hover:text-black"
        }`}
      >
        {open ? (
          <X className="w-6 h-6" />
        ) : t("assistant_icon") ? (
          <img src={t("assistant_icon")} alt="Assistente vocale" className="w-full h-full object-cover" data-testid="assistant-widget-icon" />
        ) : (
          <Mic className="w-6 h-6" />
        )}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            data-testid="assistant-widget-panel"
            className="fixed bottom-24 right-6 z-50 w-[calc(100vw-3rem)] max-w-sm bg-[#111111]/95 backdrop-blur-2xl border border-white/15 rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div>
                <p className="font-display font-bold text-sm">{ui.widget_title}</p>
                <p className="text-[11px] text-zinc-500 font-mono tracking-wider">{STATUS_LABELS[lang][a.state]}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={a.toggleMute}
                  data-testid="widget-mute-toggle"
                  aria-label="Audio"
                  className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
                >
                  {a.muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
                <button
                  onClick={a.toggleMic}
                  data-testid="widget-mic-button"
                  aria-label="Microfono"
                  className={`relative w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                    a.state === "listening" ? "bg-[#007AFF] text-white" : "bg-white text-black hover:bg-[#007AFF] hover:text-white"
                  }`}
                >
                  {a.state === "listening" ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="px-5 py-2 flex items-end justify-center gap-1 h-8" data-testid="widget-equalizer">
              {BARS.map((b) => (
                <span
                  key={b}
                  className={`w-1 rounded-full ${active ? "eq-bar bg-[#007AFF]" : "bg-white/15"}`}
                  style={{ height: `${12 + (b % 3) * 6}px`, animationDelay: `${b * 0.11}s` }}
                />
              ))}
            </div>

            <div ref={transcriptRef} className="px-4 py-3 h-64 overflow-y-auto space-y-3" data-testid="widget-transcript">
              {a.messages.length === 0 && (
                <div className="flex flex-wrap gap-2 pt-2" data-testid="widget-suggestions">
                  {ui.widget_suggestions.map((s, i) => (
                    <button
                      key={s}
                      onClick={() => a.sendMessage(s)}
                      data-testid={`widget-suggestion-${i}`}
                      className="rounded-full border border-white/15 px-3.5 py-2 text-xs text-zinc-400 hover:text-white hover:border-[#007AFF]/60 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
              {a.messages.map((m, i) => (
                <div key={i} className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "assistant" && t("assistant_icon") && (
                    <img src={t("assistant_icon")} alt="" className="w-6 h-6 rounded-full object-cover shrink-0 ring-1 ring-[#007AFF]/50" data-testid={`widget-avatar-${i}`} />
                  )}
                  <div
                    data-testid={`widget-message-${i}`}
                    className={`max-w-[85%] px-4 py-2.5 text-[13px] leading-relaxed rounded-2xl ${
                      m.role === "user"
                        ? "bg-[#007AFF] text-white rounded-br-sm"
                        : "bg-white/[0.06] border border-white/10 text-zinc-200 rounded-bl-sm"
                    }`}
                  >
                    {m.text || (
                      <span className="inline-flex gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-bounce" />
                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-bounce" style={{ animationDelay: "0.15s" }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-bounce" style={{ animationDelay: "0.3s" }} />
                      </span>
                    )}
                    {m.greeting && (
                      <button
                        onClick={() => a.speak(m.text)}
                        data-testid="greeting-replay-button"
                        className="mt-2 flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-[#007AFF] hover:text-white transition-colors"
                      >
                        <Play className="w-3 h-3" /> {ui.greeting_replay}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                a.sendMessage(input);
                setInput("");
              }}
              className="p-3 border-t border-white/10 flex gap-2"
              data-testid="widget-text-form"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={ui.widget_ph}
                data-testid="widget-text-input"
                className="flex-1 bg-white/[0.05] border border-white/15 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#007AFF]/60 transition-colors"
              />
              <button
                type="submit"
                disabled={!input.trim() || a.state === "thinking"}
                data-testid="widget-send-button"
                aria-label="Invia"
                className="w-10 h-10 rounded-full bg-white text-black flex items-center justify-center hover:bg-[#007AFF] hover:text-white transition-colors disabled:opacity-40 shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
